import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { EventGames } from '../objects/event-games';
import { FoursomeGames } from '../objects/foursome-games';
import { Round } from '../objects/round';
import { StablefordPoints } from '../objects/stableford-points';
import { ChicagoPoints } from '../objects/chicago-points';
import { eventRecurrence } from '../objects/event-recurrence';
import { MatchPlayMatchup } from '../objects/match-play-matchup';
import { RoundVerify } from '../objects/round-verify';
import { Player } from '../objects/player';
import { GGKResponse } from 'src/app/objects/ggkResponse';
import { GroupEvent } from '../objects/group-event';
import { EventPlayingFormat } from 'src/app/objects/event-game-config';
import { SaveEventPlayingFormatRequest } from 'src/app/objects/save-event-playing-format-request';
import { SubsetComparisonResult } from '../models/games/tiebreaker.models';

const API_URL = environment.BASE_API_URL + 'GGKEventWebService.asmx/';
const GGK_API_KEY = environment.GGK_API_KEY;


@Injectable()
export class EventService {

  constructor(private http: HttpClient) {
  }

  post<T>(url: string, payload: any) {
    return this.http.post<T>(url, payload, { params: { 'api_key': GGK_API_KEY } })
  }

  getCalendarEvents(start_time: string, end_time: string, event_type: string, player_id: string): Observable<any> {

    let payload = { p_start: start_time, p_end: end_time, p_type: event_type, p_id: player_id };
    let url = API_URL + 'ggkGetCalendarEvents';

    return this.post<any>(url, payload);
  }

  getEventOnlyLiveResults(group_event_id: string, type: string) {
    let payload = { group_event_id: group_event_id, type: type, deep: false };
    let url = API_URL + 'getEventOnlyLiveResults';

    return this.post<any>(url, payload);

  }

  getLiveResults(_group_event_id: string, _type: string) {
    let payload = { group_event_id: _group_event_id, type: _type };
    let url = API_URL + 'getLiveResults';

    return this.post<any>(url, payload);
  }

  getResults(scoring_type: string, master_event_id: string, board_key: string, group_event_id: string) {
    const payload = {
      scoring_type: scoring_type,
      master_event_id: master_event_id,
      board_key: board_key,
      group_event_id: group_event_id
    };
    const url = API_URL + 'getResults';
    return this.post<any>(url, payload);
  }

  getGameResults(master_event_id: string, game_id: string) {
    const payload = {
      master_event_id: master_event_id,
      game_id: game_id
    };
    const url = API_URL + 'getGameResults';
    return this.post<any>(url, payload);
  }

  getLeaderboardResults(master_event_id: string, game_id: string, scope: string, result_flavors: string[], group_event_id: string) {
    const payload = {
      master_event_id: master_event_id,
      game_id: game_id,
      scope: scope,
      result_flavor: '',
      result_flavors: JSON.stringify(result_flavors),
      group_event_id: group_event_id
    };
    const url = API_URL + 'getLeaderboardResults';
    return this.post<any>(url, payload);
  }

  getEventVerifyRounds(group_event_id: string, event_scoring: string) {
    let payload = { group_event_id: group_event_id, event_scoring: event_scoring };
    let url = API_URL + 'getEventVerifyRounds';

    return this.post<any>(url, payload);
  }

  getEventVerifyRoundsByTeamSize(group_event_id: string, players_per_team: number) {
    const payload = { group_event_id: group_event_id, players_per_team: players_per_team.toString() };
    const url = API_URL + 'getEventVerifyRoundsByTeamSize';
    return this.post<any>(url, payload);
  }

  getEventInfo(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getGroupEventInfo';

    return this.post<any>(url, payload);
  }

  getEventBasics(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getEventBasics';

    return this.post<any>(url, payload);
  }

  ggkGetEventRounds(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'ggkGetEventRounds';

    return this.post<any>(url, payload);
  }

  getEventWinners(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getEventWinnersJSON';

    return this.post<any>(url, payload);
  }

  getSingleRoundWinners(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getSingleRoundWinners';

    return this.post<any>(url, payload);
  }


  getEventSkins(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getEventSkins';

    return this.post<any>(url, payload);
  }

  getEventExtraGamesDetails(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getExtraGamesHeaders';

    return this.post<any>(url, payload);
  }

  getPaceOfPlayReport(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getPaceOfPlayReport';

    return this.post<any>(url, payload);
  }

  getEventExtraGamesResults(group_event_id: string, game_id: string, flight: string) {
    let payload = {
      group_event_id: group_event_id,
      game_id: game_id,
      flight: flight
    };
    let url = API_URL + 'getExtraGamesResults';

    return this.post<any>(url, payload);
  }

  createEvent(_date: string, _time: string, _eventName: string, _group_id: string, _course_id: string, _tees_id: string, event_scoring: string, number_of_holes: string) {
    let payload = {
      dateText: _date, timeText: _time, eventName: _eventName,
      group_id: _group_id, player_count: '0', number_of_holes: number_of_holes,
      event_scoring: event_scoring, course_id: _course_id, tees_id: _tees_id
    };
    let url = API_URL + 'createEvent';

    return this.post<any>(url, payload);
  }

  createMultiRoundEvent(_date: string, _time: string, _eventName: string, _group_id: string, _is_ryder_cup: string) {
    let payload = {
      dateText: _date, timeText: _time, eventName: _eventName,
      group_id: _group_id, player_count: '0', number_of_holes: '18',
      event_scoring: '1', is_ryder_cup: _is_ryder_cup
    };
    let url = API_URL + 'createMultiRoundEvent';

    return this.post<any>(url, payload);
  }

  ggkCreateMultiRoundEvent(group_event: GroupEvent) {
    let payload = { "group_event" : group_event };
    let url = API_URL + 'ggkCreateMultiRoundEvent';

    return this.post<any>(url, payload);
  }

  ggkCreateMasterEvent(group_event: GroupEvent) {
    let payload = { "groupEvent" : group_event };
    let url = API_URL + 'ggkCreateMasterEvent';

    return this.post<GGKResponse>(url, payload);
  }

  ggkUpdateMasterEvent(group_event: GroupEvent) {
    let payload = { "groupEvent" : group_event };
    let url = API_URL + 'ggkUpdateMasterEvent';

    return this.post<GGKResponse>(url, payload);
  }

  getProcessStatus(guid: string) {
    let payload = {
      session_id: guid
    }
    let url = API_URL + 'getProcessStatus';

    return this.post<any>(url, payload);
  }

  addEventToMultiRound(group_event_id: string, _date: string, _time: string, _eventName: string, _group_id: string, _course_id: string, _tees_id: string, _event_scoring: string, _guid: string) {
    let payload = {
      group_event_id: group_event_id,
      dateText: _date,
      timeText: _time,
      eventName: _eventName,
      group_id: _group_id,
      player_count: '0',
      number_of_holes: '18',
      event_scoring: _event_scoring,
      course_id: _course_id,
      tees_id: _tees_id,
      guid: _guid
    };
    let url = API_URL + 'addEventToMultiRound';

    return this.post<any>(url, payload);
  }

  updateRound(roundRequest: any) {
    let payload = roundRequest;
    let url = API_URL + 'updateRound';

    return this.post<any>(url, payload);
  }

  getRoundsFromMultiRounds(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getRoundsFromMultiRounds';

    return this.post<any>(url, payload);
  }


  getAvailableEventScoring(_group_id: string) {
    let payload = { group_id: _group_id };
    let url = API_URL + 'getAvailableEventScoring';

    return this.post<any>(url, payload);
  }

  getAvailablePointSystem(_group_id: string) {
    let payload = { group_id: _group_id };
    let url = API_URL + 'getAvailablePointSystem';

    return this.post<any>(url, payload);
  }

  getAvailablePointSystemValues(
    group_id: string,
    point_system_id: number,
    points?: Array<{ placement: number; point_value: number }>,
    point_system_name?: string
  ) {
    const payload: any = { group_id: group_id, point_system_id: point_system_id };
    if (Array.isArray(points)) {
      payload.points = points;
    }
    if (point_system_name !== undefined) {
      payload.point_system_name = point_system_name;
    }
    let url = API_URL + 'getAvailablePointSystemValues';

    return this.post<any>(url, payload);
  }

  savePointSystem(request: {
    group_id: string;
    point_system_id: number;
    point_system_name: string;
    points: Array<{ placement: number; point_value: number }>;
  }) {
    let payload = { request: request };
    let url = API_URL + 'savePointSystem';
    return this.post<any>(url, payload);
  }

  removePointSystem(group_id: string, point_system_id: number) {
    let payload = { group_id: group_id, point_system_id: point_system_id };
    let url = API_URL + 'removePointSystem';
    return this.post<any>(url, payload);
  }

  getEventTypes(is_master: number) {
    let payload = { is_master: is_master };
    let url = API_URL + 'getEventTypes';

    return this.post<any>(url, payload);
  }

  getPayingSpots(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getPayingSpots';

    return this.post<any>(url, payload);
  }

  getEventGames(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getEventGames';

    return this.post<any>(url, payload);
  }

  ggkGetAllEventGames(_group_event_id: string) {
    let payload = { master_event_id: _group_event_id };
    let url = API_URL + 'ggkGetAllEventGames';

    return this.post<any>(url, payload);
  }

  getggkEventScoring(group_id: string) {
    let payload = { group_id: group_id };
    let url = API_URL + 'getggkEventScoring';
    return this.post<any>(url, payload);
  }

  ggkAddEventGame(request: SaveEventPlayingFormatRequest) {
    const payload: any = { request: request };
    const url = API_URL + 'ggkAddEventGame';
    return this.post<GGKResponse>(url, payload);
  }

  ggkGetEventGames(groupEventId: number) {
    let payload = { groupEventId: groupEventId };
    let url = API_URL + 'ggkGetEventGames';
    return this.post<GGKResponse>(url, payload);
  }

  ggkRemoveEventGameById(gameId: number) {
    let payload = { gameId: gameId };
    let url = API_URL + 'ggkRemoveEventGameById';
    return this.post<GGKResponse>(url, payload);
  }

  ggkUpdateEventGame(request: SaveEventPlayingFormatRequest) {
    const payload: any = { request: request };
    const url = API_URL + 'ggkUpdateEventGame';
    return this.post<GGKResponse>(url, payload);
  }

  saveRegistrationDetails(group_event_id: string, registration_start: string, registration_end: string, hide_leaderboard: string){
    let payload = {
      group_event_id: group_event_id,
      registration_start: registration_start,
      registration_end: registration_end,
      hide_leaderboard: hide_leaderboard,
    }
    let url = API_URL + 'saveRegistrationDetails';

    return this.post<any>(url, payload);
  }

  saveEventBasics(group_event_id: string, dateText: string, timeText: string, eventName: string,
    foursome_count: string, point_system: string, is_private: string, event_type?: string) {
    let payload = {
      group_event_id: group_event_id,
      dateText: dateText,
      timeText: timeText,
      eventName: eventName,
      foursome_count: foursome_count,
      point_system: point_system,
      is_private: is_private
    };
    
    if (event_type) {
      payload['event_type'] = event_type;
    }
    
    let url = API_URL + 'saveEventBasics';

    return this.post<any>(url, payload);
  }

  saveEventGames(event_games: EventGames) {
    let url = API_URL + 'saveEventGames';

    return this.post<any>(url, event_games);
  }


  saveEventBasicsNoTees(group_event_id: string, dateText: string, timeText: string, eventName: string,
    foursome_count: string, point_system: string, is_private: string, holes_to_play: string, event_scoring: string, event_type: string) {
    let payload = {
      group_event_id: group_event_id,
      dateText: dateText,
      timeText: timeText,
      eventName: eventName,
      foursome_count: foursome_count,
      point_system: point_system,
      is_private: is_private,
      holes_to_play: holes_to_play,
      event_scoring: event_scoring,
      event_type: event_type
    };
    let url = API_URL + 'saveEventBasicsNoTees';

    return this.post<any>(url, payload);
  }

  saveEventBasicsWithTees(group_event_id: string, dateText: string, timeText: string, eventName: string,
    foursome_count: string, point_system: string, is_private: string, holes_to_play: string, event_scoring: string, event_type: string, course_id: string, tees_id: string) {
    let payload = {
      group_event_id: group_event_id,
      dateText: dateText,
      timeText: timeText,
      eventName: eventName,
      foursome_count: foursome_count,
      point_system: point_system,
      is_private: is_private,
      holes_to_play: holes_to_play,
      event_scoring: event_scoring,
      event_type: event_type,
      course_id: course_id,
      tees_id: tees_id
    };
    let url = API_URL + 'saveEventBasicsWithTees';

    return this.post<any>(url, payload);
  }

  adminSaveRound(payload: Round) {
    let url = API_URL + 'adminSaveRound';

    return this.post<any>(url, payload);
  }

  deleteEvent(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'deleteEvent';

    return this.post<any>(url, payload);
  }

  getAdditionalScoringGames(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getAdditionalScoringGames';

    return this.post<any>(url, payload);
  }

  getPayFrontBackTotalWinners(group_event_id: number){
    let payload= { group_event_id: group_event_id, type: "1"};
    let url = API_URL + 'getPayFrontBackTotalWinners';

    return this.post<any>(url, payload);
  }

  getPayFrontBackTotal(group_event_id: number){
    let payload= { group_event_id: group_event_id };
    let url = API_URL + 'getPayFrontBackTotal';

    return this.post<any>(url, payload);
  }

  deletePayFrontBackTotal(group_event_id: number){
    let payload= { group_event_id: group_event_id };
    let url = API_URL + 'deletePayFrontBackTotal';

    return this.post<any>(url, payload);
  }


  saveUpdatePayFrontBackTotal(group_event_id : number, buyin : number, team_game : boolean, team_size: number, pay_count: number){
    let payload = {group_event_id : group_event_id, buyin : buyin, team_game : team_game, team_size: team_size, pay_count: pay_count}
    let url = API_URL + 'saveUpdatePayFrontBackTotal';

    return this.post<any>(url, payload);
  }


  addExtraGame(_group_event_id: string, _scoring_details_id: string) {
    let payload = { group_event_id: _group_event_id, scoring_details_id: _scoring_details_id };
    let url = API_URL + 'addExtraGame';

    return this.post<any>(url, payload);
  }

  deleteExtraGame(_group_event_id: string, _scoring_details_id: string) {
    let payload = { group_event_id: _group_event_id, scoring_details_id: _scoring_details_id };
    let url = API_URL + 'deleteExtraGame';

    return this.post<any>(url, payload);
  }

  getEventExtraGames(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getExtraGames';

    return this.post<any>(url, payload);
  }

  getEventFoursomeGames(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getEventFoursomeGames';

    return this.post<any>(url, payload);
  }

  saveEventFoursomeGames(eventFoursomeGames: FoursomeGames) {
    let url = API_URL + 'saveEventFoursomeGames';
    return this.post<any>(url, eventFoursomeGames);
  }

  getAvailableGroupPlayers(_group_id: string, _group_event_id: string) {
    let payload = { group_id: _group_id, group_event_id: _group_event_id };
    let url = API_URL + 'getAvailableGroupPlayersV2';

    return this.post<any>(url, payload);
  }


  getGroupEventPaymentStatus(_group_id: string, _group_event_id: string) {
    let payload = { group_id: _group_id, group_event_id: _group_event_id };
    let url = API_URL + 'getGroupEventPaymentStatus';

    return this.post<any>(url, payload);
  }

  getGroupMembershipDetails(membership_id: string) {
    let payload = { membership_id: membership_id };
    let url = API_URL + 'getGroupMembershipDetails';

    return this.post<any>(url, payload);
  }

  submitTournamentPayment(group_event_id: string, token: string, amount: number, note: string, player_id: string) {
    let payload = { group_event_id: group_event_id, token: token, amount: amount, note: note, player_id: player_id };
    let url = API_URL + 'submitTournamentPayment';

    return this.post<any>(url, payload);
  }

  getEventPlayers(_group_event_id: string) {
    let payload = { group_event_id: _group_event_id };
    let url = API_URL + 'getEventPlayers';

    return this.post<any>(url, payload);
  }

  // Some calls use the master_event_id, but the backend expects it in group_event_id.
  getEventPlayersByMasterEventId(master_event_id: string) {
    let payload = { group_event_id: master_event_id };
    let url = API_URL + 'getEventPlayersAllRounds';

    return this.post<any>(url, payload);
  }

  getEventPlayersQuota(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getEventPlayersQuota';

    return this.post<any>(url, payload);
  }

  addPlayerToEvent(_group_event_id: string, _player_id: string, _force_add: boolean) {
    let payload = { group_event_id: _group_event_id, player_id: _player_id, forceAdd: _force_add };
    let url = API_URL + 'addPlayerToEvent';

    return this.post<any>(url, payload);
  }

  addPlayerToEventAsGuest(_group_event_id: string, _player_id: string) {
    let payload = { group_event_id: _group_event_id, player_id: _player_id };
    let url = API_URL + 'addPlayerToEventAsGuest';

    return this.post<any>(url, payload);
  }

  registerCTP(group_event_id: string, player_id: string) {
    let payload = { group_event_id: group_event_id, player_id: player_id };
    let url = API_URL + 'registerCTP';

    return this.post<any>(url, payload);
  }

  addPlayersToEvent(_group_event_id: string, _player_id: string) {
    let payload = { group_event_id: _group_event_id, playerIds: _player_id };
    let url = API_URL + 'addPlayersToEvent';

    return this.post<any>(url, payload);
  }

  addPlayerToFoursome(group_event_id: string, event_id: string, player_id: string, forceNewFoursome: boolean) {
    let payload = { group_event_id: group_event_id, event_id: event_id, player_id: player_id, forceNewFoursome: forceNewFoursome };
    let url = API_URL + 'addPlayerToFoursome';

    return this.post<any>(url, payload);
  }

  // Create a new foursome/tee time for an event (backend returns the new event_id).
  ggkCreateFoursome(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'ggkCreateFoursome';

    return this.post<any>(url, payload);
  }

  // Remove an existing foursome/tee time.
  removeFoursome(event_id: string) {
    let payload = { event_id: event_id };
    let url = API_URL + 'removeFoursome';

    return this.post<any>(url, payload);
  }

  createFoursome(group_event_id: string, player_id: string) {
    let payload = { group_event_id: group_event_id, player_id: player_id };
    let url = API_URL + 'createFoursome';

    return this.post<any>(url, payload);
  }

  moveToNewFoursome(group_event_id: string, player_id: string) {
    let payload = { group_event_id: group_event_id, player_id: player_id };
    let url = API_URL + 'movePlayerToNewFoursome';

    return this.post<any>(url, payload);
  }

  addPlayersToFoursome(group_event_id: string, event_id: string, players: Player[]) {
    let payload = { group_event_id: group_event_id, event_id: event_id, players: players };
    let url = API_URL + 'addPlayersToFoursome';

    return this.post<any>(url, payload);
  }

  removePlayerFromEvent(_group_event_id: string, _player_id: string, _force_action: boolean) {
    let payload = { group_event_id: _group_event_id, player_id: _player_id, forceAction: _force_action };
    let url = API_URL + 'removePlayerFromEvent';

    return this.post<any>(url, payload);
  }

  removePlayersFromEvent(_group_event_id: string, _player_id: string) {
    let payload = { group_event_id: _group_event_id, playerIds: _player_id };
    let url = API_URL + 'removePlayersFromEvent';

    return this.post<any>(url, payload);
  }

  reinstatePlayerToEvent(group_event_id: string, player_id: string) {
    let payload = { group_event_id: group_event_id, player_id: player_id };
    let url = API_URL + 'reinstatePlayerToEvent';

    return this.post<any>(url, payload);
  }

  getFinancialReport(player_id: string, group_event_id: string) {
    const payload = { player_id, group_event_id };
    const url = API_URL + 'getFinancialReport';
    return this.post<any>(url, payload);
  }

  getStripeReceiptUrl(stripe_charge_id: string, group_event_id: string) {
    const payload = { stripe_charge_id, group_event_id };
    const url = API_URL + 'getStripeReceiptUrl';
    return this.post<any>(url, payload);
  }

  saveFlight(group_event_id: string, flight: string, roundArray: Round[]) {
    var player_ids = roundArray.map(x => x.player_id).join(',');
    let payload = { group_event_id: group_event_id, player_ids: player_ids, flight: flight };

    let url = API_URL + 'saveFlight';

    return this.post<any>(url, payload);
  }

  saveFlightByPlayerIds(group_event_id: string, player_ids: string, flight: string) {
    let payload = { group_event_id: group_event_id, player_ids: player_ids, flight: flight };
    let url = API_URL + 'saveFlight';

    return this.post<any>(url, payload);
  }

  saveFlightData(flights: any[], group_event_id: string) {
    let payload = { flights: flights, group_event_id: group_event_id };
    let url = API_URL + 'saveFlightData';

    return this.post<any>(url, payload);
  }

  getEventFlights(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getEventFlights';

    return this.post<any>(url, payload);
  }

  getFlightedData(group_event_id: string, group_id: string, force_reset: boolean, use_course_hcp: boolean, nbr_of_flights: number) {
    let payload = { group_event_id: group_event_id, group_id: group_id, force_reset: force_reset, use_course_hcp: use_course_hcp, nbr_of_flights: nbr_of_flights };

    let url = API_URL + 'getFlightedData';

    return this.post<any>(url, payload);
  }

  updateRoundAndGames(round_id: string, tees_id: string, index: string, off_low: string, in_prize_pool: string, net_skin: string, gross_skin: string) {
    let payload = {
      round_id: round_id, tees_id: tees_id, player_index: index, off_low: off_low,
      in_prize_pool: in_prize_pool, net_skin: net_skin, gross_skin: gross_skin
    };
    let url = API_URL + 'updateRoundAndGames';

    return this.post<any>(url, payload);
  }

  movePlayer(round_id: string, event_id: string, team_id: string) {
    let payload = { round_id: round_id, event_id: event_id, team_id: team_id };
    let url = API_URL + 'movePlayer';

    return this.post<any>(url, payload);
  }

  changeStartingHoleOrderAndTime(event_id: string, new_starting_hole: string, new_starting_order: string, new_start_time: string, display_order: string = '') {
    let payload = {
      event_id: event_id,
      new_starting_hole: new_starting_hole,
      new_starting_order: new_starting_order,
      new_start_time: new_start_time,
      display_order: display_order
    };
    let url = API_URL + 'changeStartingHoleOrderAndTime';

    return this.post<any>(url, payload);
  }

  changeStartingHoleAndOrder(event_id: string, new_starting_hole: string, new_starting_order: string) {
    let payload = {
      event_id: event_id,
      new_starting_hole: new_starting_hole,
      new_starting_order: new_starting_order
    };
    let url = API_URL + 'changeStartingHoleAndOrder';

    return this.post<any>(url, payload);
  }

  saveAndVerify(round_id: string, holes_score: string, holes_hcp: string, holes_par: string) {
    let payload = {
      round_id: round_id,
      holes_score: holes_score,
      holes_hcp: holes_hcp,
      holes_par: holes_par
    };
    let url = API_URL + 'saveAndVerify';

    return this.post<any>(url, payload);
  }

  saveStats(round_id: string, putts: string, putts_average: string, girs: string, girs_percent: string, fairways: string, fairway_percent: string) {
    let payload = {
      round_id: round_id,
      putts : putts,
      putts_average: putts_average,
      girs: girs,
      girs_percent: girs_percent,
      fairways: fairways,
      fairway_percent: fairway_percent
    };
    let url = API_URL + 'saveStats';

    return this.post<any>(url, payload);
  }

  performPostVerifyActions(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'performPostVerifyActions';

    return this.post<any>(url, payload);
  }

  getEventSponsors(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getEventSponsors';

    return this.post<any>(url, payload);
  }

  
  setEventStatus(group_event_id: string, status: string) {
    let payload = { group_event_id: group_event_id, status: status };
    let url = API_URL + 'setEventStatus';

    return this.post<any>(url, payload);
  }

  removeEventSponsor(id: string) {
    let payload = { id: id };
    let url = API_URL + 'removeEventSponsor';

    return this.post<any>(url, payload);
  }

  getAllEventsByGroupType(player_id: string, group_type: string, back_days: string, forward_days: string) {
    let payload = { player_id: player_id, group_type: group_type, back_days: back_days, forward_days: forward_days };
    let url = API_URL + 'getAllEventsByGroupType';

    return this.post<any>(url, payload);
  }

  getGroupEvents(player_id: string, group_id: string, month: string, year: string) {
    let payload = { player_id: player_id, group_id: group_id, month: month, year: year };
    let url = API_URL + 'getGroupEventsJson';

    return this.post<any>(url, payload);
  }

  getGroupEventsBySeasonId(player_id: string, group_id: string, season_id: string) {
    let payload = { player_id: player_id, group_id: group_id, season_id: season_id };
    let url = API_URL + 'getGroupEventsBySeasonIdJson';

    return this.post<any>(url, payload);
  }

  getStablefordPoints(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getStablefordPoints';

    return this.post<any>(url, payload);
  }

  setStablefordPoints(points: StablefordPoints) {
    let url = API_URL + 'setStablefordPoints';
    let payload = {
      group_event_id: points.group_event_id,
      albatros: points.albatros,
      eagle: points.eagle,
      birdey: points.birdey,
      par: points.par,
      bogey: points.bogey,
      doubleBogey: points.double,
      other: points.other
    }

    return this.post<any>(url, payload);
  }

  getChicagoPoints(group_event_id: string, event_scoring: string) {
    let payload = { group_event_id: group_event_id, event_scoring: event_scoring };
    let url = API_URL + 'getChicagoPoints';

    return this.post<any>(url, payload);
  }

  setChicagoPoints(points: ChicagoPoints) {
    let url = API_URL + 'setChicagoPoints';
    let payload = {
      group_event_id: points.group_event_id,
      start_score: points.start_score,
      albatros: points.albatros,
      eagle: points.eagle,
      birdey: points.birdey,
      par: points.par,
      bogey: points.bogey,
      doublebogey: points.doublebogey,
      other: points.other
    }

    return this.post<any>(url, payload);
  }

  copyEvent(group_event_id: string, date: string) {
    let payload = { group_event_id: group_event_id, date: date };
    let url = API_URL + 'copyEvent';

    return this.post<any>(url, payload);
  }

  getPricedItemTypes(group_id: string) {
    let payload = { group_id: group_id };
    let url = API_URL + 'getPricedItemTypes';

    return this.post<any>(url, payload);
  }

  getPricedList(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getPricedList';

    return this.post<any>(url, payload);
  }

  addPricedItem(group_event_id: string, item_type: string, item_price_in_cents: string, optional: string) {
    let payload = { group_event_id: group_event_id, item_type: item_type, item_price: item_price_in_cents, optional: optional };
    let url = API_URL + 'addPricedItem';

    return this.post<any>(url, payload);
  }

  removePricedItem(item_id: string) {
    let payload = { item_id: item_id };
    let url = API_URL + 'removePricedItem';

    return this.post<any>(url, payload);
  }

  payEventEntryFee(token: string, amount: number, note: string, player_id: string, group_event_id: string) {
    let payload = {
      token: token,
      amount: amount,
      note: note,
      player_id: player_id,
      group_event_id: group_event_id,
    };
    let url = API_URL + 'payEventEntryFeeV2';

    return this.post<any>(url, payload);
  }

  registerAfterPayment(group_event_id: string, player_id: string,
    in_net_skins: string, in_gross_skins: string, in_prize_pool: string, ctp:string, 
    register_as_guest: boolean, credits_used: number, total_price: number) {
    let payload = {
      group_event_id: group_event_id,
      player_id: player_id,
      in_net_skins: in_net_skins,
      in_gross_skins: in_gross_skins,
      in_prize_pool: in_prize_pool,
      ctp: ctp,
      register_as_guest: register_as_guest,
      credits_used: credits_used,
      total_price: total_price
    };
    let url = API_URL + 'registerAfterPayment';

    return this.post<any>(url, payload);
  }

  getEventRegisteredPlayerCount(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getEventRegisteredPlayerCount';

    return this.post<any>(url, payload);
  }

  saveEventRecurrence(payload: eventRecurrence) {
    let url = API_URL + 'saveEventRecurrence';

    return this.post<any>(url, payload);
  }

  getEventRecurrence(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getEventRecurrence';

    return this.post<any>(url, payload);
  }

  isPlayerRegistered(group_event_id: string, player_id: string) {
    let payload = { group_event_id: group_event_id, player_id: player_id };
    let url = API_URL + 'isPlayerRegistered';

    return this.post<any>(url, payload);
  }

  sendEventInviteGuestEmail(group_event_id: string, sender_player_id: string, guest_email: string) {
    let payload = { group_event_id: group_event_id, sender_player_id: sender_player_id, guest_email: guest_email };
    let url = API_URL + 'sendEventInviteGuestEmail';

    return this.post<any>(url, payload);
  }

  loadRecordFromFile(group_id: string, group_event_id: string, first: string, last: string, email: string, start_hole: string, start_order: string, index: string, flight: string, asGuest: string) {
    let payload = {
      group_id: group_id,
      group_event_id: group_event_id,
      first: first,
      last: last,
      email: email,
      start_hole: start_hole,
      start_order: start_order,
      index: index,
      flight: flight,
      asGuest: asGuest
    };
    let url = API_URL + 'loadRecordFromFile';

    return this.post<any>(url, payload);
  }

  removeAllPlayersFromEvent(group_event_id: string) {
    let payload = { group_event_id: group_event_id }
    let url = API_URL + 'removeAllPlayersFromEvent';

    return this.post<any>(url, payload);
  }

  getMatchPlayMatchUps(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getMatchPlayMatchUps';

    return this.post<any>(url, payload);
  }

  saveMatchPlayMatch(payload: MatchPlayMatchup) {
    let url = API_URL + 'saveMatchPlayMatch';

    return this.post<any>(url, payload);
  }

  getMatchPlayTeamResult(group_event_id: string, type: string) {
    let payload = { group_event_id: group_event_id, type: type };
    let url = API_URL + 'getMatchPlayTeamResult';

    return this.post<any>(url, payload);
  }

  getMatchPlayCompletedTeamResult(group_event_id: string, type: string) {
    let payload = { group_event_id: group_event_id, type: type };
    let url = API_URL + 'getMatchPlayCompletedTeamResult';

    return this.post<any>(url, payload);
  }

  updateTeamHandicap(group_event_id: string, event_scoring: string) {
    let payload = { group_event_id: group_event_id, event_scoring: event_scoring };
    let url = API_URL + 'updateTeamHandicap';

    return this.post<any>(url, payload);
  }

  resetTeamHandicap(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'resetTeamHandicap';

    return this.post<any>(url, payload);
  }

  playOffLow(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'playOffLow';

    return this.post<any>(url, payload);
  }

  processPenalty(payload: RoundVerify) {
    let url = API_URL + 'processPenalty';

    return this.post<any>(url, payload);
  }

  createTwosomes(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'createTwosomes';

    return this.post<any>(url, payload);
  }

  postScore(player_id: string, date: string, course_id: string, tees_id: string, score: string, type: string, number_of_holes: string) {
    let payload = { player_id: player_id, date: date, course_id: course_id, tees_id: tees_id, score: score, type: type, nbr_of_holes: number_of_holes };
    let url = API_URL + 'postScore';

    return this.post<any>(url, payload);
  }

  postScoreManually(player_id: string, date: string, course_name: string, slope: string, rating: string, score: string, type: string, number_of_holes: string) {
    let payload = { player_id: player_id, date: date, course_name : course_name, slope: slope, rating: rating, score: score, type: type, nbr_of_holes: number_of_holes };
    let url = API_URL + 'postScoreManuallyApp';

    return this.post<any>(url, payload);
  }

  getPlayerRound(group_event_id: string, player_id: string){
    let payload = {group_event_id : group_event_id, player_id: player_id};
    let url = API_URL + "getPlayerRound";

    return this.post<any>(url, payload);
  }

  getProjectionSetup(group_event_id: string){
    let payload = {group_event_id : group_event_id };
    let url = API_URL + "getProjectionSetup";

    return this.post<any>(url, payload);
  }

  saveProjectionSetup(group_event_id: string, projectionSetupJson: string){
    let payload = {group_event_id : group_event_id, projectionSetupJson: projectionSetupJson };
    let url = API_URL + "saveProjectionSetup";

    return this.post<any>(url, payload);
  }

  getNetProjectedStandings(group_event_id: string){
    let payload = {group_event_id : group_event_id };
    let url = API_URL + "getNetProjectedStandings";

    return this.post<any>(url, payload);
  }

  getPairingRequests(group_event_id: string){
    let payload = {group_event_id : group_event_id };
    let url = API_URL + "getPairingRequests";

    return this.post<any>(url, payload);
  }

  deletePairingRequest(event_pairing_id: string){
    let payload = { event_pairing_id: event_pairing_id };
    let url = API_URL + "deletePairingRequest";

    return this.post<any>(url, payload);
  }

  clearResultsCache(group_event_id: string){
    let payload = { group_event_id: group_event_id };
    let url = API_URL + "clearResultsCache";

    return this.post<any>(url, payload);
  }

  loadEventPoints(group_id, group_event_id, add, fileData){
    let payload = {
      group_id: group_id,
      group_event_id : group_event_id,
      add_points: add,
      fileData: fileData
    };
    let url = API_URL + "loadEventPoints";
    
    return this.post<any>(url, payload);
  }

  getPayoutForEvent(group_event_id, number_of_spots, round_type){
    let payload = {
      group_event_id : group_event_id,
      number_of_spots: number_of_spots,
      round_type: round_type
    };
    let url = API_URL + "getPayoutsForAllFlights";
    
    return this.post<any>(url, payload);
  }

  resetPayoutForEvent(group_event_id, round_type){
    let payload = {
      group_event_id : group_event_id,
      round_type: round_type
    };
    let url = API_URL + "resetPayoutForEvent";
    
    return this.post<any>(url, payload);
  }

  savePayoutForFlight(group_event_id, number_of_spots, pay_type, round_type, pay_string, flight){
    let payload = {
      group_event_id : group_event_id,
      number_of_spots: number_of_spots,
      pay_type: pay_type,
      round_type: round_type,
      pay_string: pay_string,
      flight: flight
    };
    let url = API_URL + "savePayoutForFlight";
    
    return this.post<any>(url, payload);
  }

  addPlayerToCTP(group_event_id, player_id){
    let payload = {
      group_event_id : group_event_id,
      player_id: player_id
     };
    let url = API_URL + "addPlayerToCTP";

    return this.post<GGKResponse>(url, payload);
  }

  removePlayerFromCTP(group_event_id, player_id){
    let payload = {
      group_event_id : group_event_id,
      player_id: player_id
     };
    let url = API_URL + "removePlayerFromCTP";

    return this.post<GGKResponse>(url, payload);
  }

  setContestExclusion(round_id: string, exclusion: string){
    let payload = { round_id: round_id, exclusion: exclusion };
    let url = API_URL + 'setContestExclusion';

    return this.post<any>(url, payload);
  }

  // Event Pricing Methods
  getGGKEventPricingTypes() {
    let url = API_URL + 'getGGKEventPricingTypes';
    return this.post<GGKResponse>(url, {});
  }

  getAllEventPricing(master_event_id: number) {
    let payload = { master_event_id: master_event_id };
    let url = API_URL + 'getAllEventPricing';
    return this.post<GGKResponse>(url, payload);
  }

  getRegistrationPaymentItems(group_event_id: string, player_id: string) {
    let payload = { group_event_id: group_event_id, player_id: player_id };
    let url = API_URL + 'getRegistrationPaymentItems';
    return this.post<GGKResponse>(url, payload);
  }

  registerAndInvoiceItems(group_event_id: string, player_id: string, selected_event_pricing_ids: string) {
    let payload = {
      group_event_id: group_event_id,
      player_id: player_id,
      selected_event_pricing_ids: selected_event_pricing_ids,
      days_until_due: '7'
    };
    let url = API_URL + 'editAndInvoice';
    return this.post<any>(url, payload);
  }

  registerWithPayment(group_event_id: string, player_id: string, selected_event_pricing_ids: string, stripe_token: string, credits_used_cents: string) {
    let payload = {
      group_event_id: group_event_id,
      player_id: player_id,
      selected_event_pricing_ids: selected_event_pricing_ids,
      stripe_token: stripe_token,
      credits_used_cents: credits_used_cents
    };
    let url = API_URL + 'registerWithPayment';
    return this.post<any>(url, payload);
  }

  registerAfterPaymentNew(group_event_id: string, player_id: string, selected_event_pricing_ids: string, credits_used_cents: string, payment_cents: string) {
    let payload = {
      group_event_id: group_event_id,
      player_id: player_id,
      selected_event_pricing_ids: selected_event_pricing_ids,
      credits_used_cents: credits_used_cents,
      payment_cents: payment_cents
    };
    let url = API_URL + 'registerAfterPaymentNew';
    return this.post<any>(url, payload);
  }

  getEventPrizePools(master_event_id: string) {
    let payload = { master_event_id: master_event_id };
    let url = API_URL + 'getEventPrizePools';
    return this.post<any>(url, payload);
  }

  getPaidPlayerIdsByGame(master_event_id: string, event_game_id: number) {
    const payload = { master_event_id: master_event_id, event_game_id: event_game_id.toString() };
    const url = API_URL + 'getPaidPlayerIdsByGame';
    return this.post<any>(url, payload);
  }

  deleteEventPricing(pricing_id: number) {
    let payload = { pricing_id: pricing_id };
    let url = API_URL + 'deleteEventPricing';
    return this.post<GGKResponse>(url, payload);
  }

  addEventPricing(group_event_id: number, event_game_id: number, item_type: number, item_price: number, optional: number) {
    let payload = { 
      group_event_id: group_event_id,
      event_game_id: event_game_id,
      item_type: item_type,
      item_price: item_price,
      optional: optional
    };
    let url = API_URL + 'addEventPricing';
    return this.post<GGKResponse>(url, payload);
  }

  updateEventPricing(pricing_id: number, item_price: number, optional: number) {
    let payload = {
      pricing_id: pricing_id,
      item_price: item_price,
      optional: optional
    };
    let url = API_URL + 'updateEventPricing';
    return this.post<GGKResponse>(url, payload);
  }

  // Event Invitation Methods
  getInvitedPlayers(group_event_id: number) {
    let payload = { group_event_id: group_event_id.toString() };
    let url = API_URL + 'getInvitedPlayers';
    return this.post<GGKResponse>(url, payload);
  }

  invitePlayerToEvent(group_event_id: number, player_id: number, invite: boolean) {
    let payload = {
      group_event_id: group_event_id.toString(),
      player_id: player_id.toString(),
      invite: invite
    };
    let url = API_URL + 'invitePlayerToEvent';
    return this.post<GGKResponse>(url, payload);
  }

  clearEventInvitations(group_event_id: number) {
    let payload = { group_event_id: group_event_id.toString() };
    let url = API_URL + 'clearEventInvitations';
    return this.post<GGKResponse>(url, payload);
  }

  getRegisteredPlayers(group_event_id: number, group_id: number) {
    let payload = {
      group_event_id: group_event_id.toString(),
      group_id: group_id.toString()
    };
    let url = API_URL + 'getRegisteredPlayers';
    return this.post<GGKResponse>(url, payload);
  }

  getRegistrationAudit(group_event_id: string) {
    let payload = { group_event_id: group_event_id };
    let url = API_URL + 'getRegistrationAudit';
    return this.post<any>(url, payload);
  }

  saveContestResults(gameId: number, masterEventId: number, results: any[]): Observable<any> {
    let payload = { gameId: gameId, masterEventId: masterEventId, resultsJson: JSON.stringify(results) };
    let url = API_URL + 'saveContestResults';
    return this.post<any>(url, payload);
  }

  getContestEligiblePlayers(masterEventId: number, gameId: number): Observable<any> {
    let payload = { masterEventId: masterEventId, gameId: gameId };
    let url = API_URL + 'getContestEligiblePlayers';
    return this.post<any>(url, payload);
  }

  getPlayerHandicapsForRound(groupEventId: string): Observable<any> {
    const payload = { group_event_id: groupEventId };
    const url = API_URL + 'getPlayerHandicapsForRound';
    return this.post<any>(url, payload);
  }

  savePlayerHandicap(groupEventId: string, playerId: number, index: number, teesId: string): Observable<any> {
    const payload = {
      group_event_id: groupEventId,
      player_id: playerId.toString(),
      index: index.toString(),
      tees_id: teesId
    };
    const url = API_URL + 'savePlayerHandicap';
    return this.post<any>(url, payload);
  }

  convertToGGKPlus(childEventId: string, sessionId?: string): Observable<any> {
    const payload: any = { child_event_id: childEventId };
    if (sessionId) payload.session_id = sessionId;
    const url = API_URL + 'convertLegacyToGGKPlus';
    return this.post<any>(url, payload);
  }

  getGamePricing(eventId: string): Observable<any> {
    const payload = { master_event_id: eventId };
    const url = API_URL + 'getGamePricing';
    return this.post<any>(url, payload);
  }

  bulkOfflineRegister(eventId: string, registrationsJson: string): Observable<any> {
    const payload = { group_event_id: eventId, registrations_json: registrationsJson };
    const url = API_URL + 'bulkOfflineRegister';
    return this.post<any>(url, payload);
  }

  importOfflinePlayers(eventId: string, playersJson: string): Observable<any> {
    const payload = { group_event_id: eventId, players_json: playersJson };
    const url = API_URL + 'importOfflinePlayers';
    return this.post<any>(url, payload);
  }

  getRegistrationPaymentCount(eventId: string): Observable<any> {
    const payload = { group_event_id: eventId };
    const url = API_URL + 'getRegistrationPaymentCount';
    return this.post<any>(url, payload);
  }

  getOfflineRegistrationSelections(eventId: string): Observable<any> {
    const payload = { group_event_id: eventId };
    const url = API_URL + 'getOfflineRegistrationSelections';
    return this.post<any>(url, payload);
  }

  reorderTeeTimes(eventIds: number[]): Observable<any> {
    const payload = { event_ids: eventIds.join(',') };
    const url = API_URL + 'reorderTeeTimes';
    return this.post<any>(url, payload);
  }

  saveTiebreakerResolution(
    epfId: string, boardKey: string, groupEventId: string,
    winnerId: string, path: string, method: string
  ): Observable<any> {
    const payload = {
      epfId: epfId,
      boardKey: boardKey,
      groupEventId: groupEventId,
      winnerId: winnerId,
      path: path,
      method: method
    };
    const url = API_URL + 'saveTiebreakerResolution';
    return this.post<any>(url, payload);
  }

  clearTiebreakerResolution(
    epfId: string, boardKey: string, groupEventId: string
  ): Observable<any> {
    const payload = {
      epfId: epfId,
      boardKey: boardKey,
      groupEventId: groupEventId
    };
    const url = API_URL + 'clearTiebreakerResolution';
    return this.post<any>(url, payload);
  }

  compareTiebreakerSubset(
    epfId: string, boardKey: string, groupEventId: string, subsetKey: string
  ): Observable<SubsetComparisonResult> {
    const payload = {
      epfId: epfId,
      boardKey: boardKey,
      groupEventId: groupEventId,
      subsetKey: subsetKey
    };
    const url = API_URL + 'compareTiebreakerSubset';
    return this.post<SubsetComparisonResult>(url, payload);
  }

  getOrphanPlayers(group_event_id: string, group_id: string): Observable<any> {
    const payload = { group_event_id, group_id };
    const url = API_URL + 'getOrphanPlayers';
    return this.post<any>(url, payload);
  }

  fixOrphanRegistration(group_event_id: string, player_id: string): Observable<any> {
    return this.post<any>(API_URL + 'fixOrphanRegistration', { group_event_id, player_id });
  }

  fixOrphanRound(group_event_id: string, player_id: string, child_event_ids: string): Observable<any> {
    return this.post<any>(API_URL + 'fixOrphanRound', { group_event_id, player_id, child_event_ids });
  }

  fixOrphanTeeTime(group_event_id: string, player_id: string, child_event_ids: string): Observable<any> {
    return this.post<any>(API_URL + 'fixOrphanTeeTime', { group_event_id, player_id, child_event_ids });
  }

  fixOrphanAudit(group_event_id: string, player_id: string): Observable<any> {
    return this.post<any>(API_URL + 'fixOrphanAudit', { group_event_id, player_id });
  }

  fixAllOrphans(group_event_id: string, group_id: string): Observable<any> {
    return this.post<any>(API_URL + 'fixAllOrphans', { group_event_id, group_id });
  }

  removeOrphanPlayer(group_event_id: string, group_id: string, player_id: string, issueCredit: boolean): Observable<any> {
    const payload = { group_event_id, group_id, player_id, issueCredit };
    const url = API_URL + 'removeOrphanPlayer';
    return this.post<any>(url, payload);
  }

  getRegistrationItemsByGameIds(masterEventId: string, eventGameIds: number[]): Observable<any> {
    const payload = { master_event_id: masterEventId, event_game_ids: JSON.stringify(eventGameIds) };
    const url = API_URL + 'getRegistrationItemsByGameIds';
    return this.post<any>(url, payload);
  }

  // Wait List API methods

  getWaitListPlayers(groupEventId: string): Observable<any> {
    const payload = { group_event_id: groupEventId };
    const url = API_URL + 'getWaitListPlayers';
    return this.post<any>(url, payload);
  }

  promoteFromWaitList(groupEventId: string, playerId: string): Observable<any> {
    const payload = { group_event_id: groupEventId, player_id: playerId };
    const url = API_URL + 'promoteFromWaitList';
    return this.post<any>(url, payload);
  }

  removeFromWaitList(groupEventId: string, playerId: string): Observable<any> {
    const payload = { group_event_id: groupEventId, player_id: playerId };
    const url = API_URL + 'removeFromWaitList';
    return this.post<any>(url, payload);
  }

  addToWaitList(groupEventId: string, playerId: string, selectedPricingIds: string, stripeToken: string): Observable<any> {
    const payload = {
      group_event_id: groupEventId,
      player_id: playerId,
      selected_event_pricing_ids: selectedPricingIds,
      stripe_token: stripeToken
    };
    const url = API_URL + 'addToWaitList';
    return this.post<any>(url, payload);
  }

  getPlayerWaitListStatus(groupEventId: string, playerId: string): Observable<any> {
    const payload = { group_event_id: groupEventId, player_id: playerId };
    const url = API_URL + 'getPlayerWaitListStatus';
    return this.post<any>(url, payload);
  }

  // ── Group Flight Config ──────────────────────────────────────────────

  ggkGetGroupFlightConfig(group_id: string, season_id?: string): Observable<any> {
    const payload: any = { group_id: parseInt(group_id, 10) || 0, season_id: season_id || '' };
    const url = API_URL + 'ggkGetGroupFlightConfig';
    return this.post<any>(url, payload);
  }

  ggkSaveGroupFlightConfig(config: {
    group_id: string;
    season_id?: string;
    assignment_method: string;
    flight_count: number;
    flight_names: string[];
    index_boundaries?: { key: string; min: number; max: number }[];
  }): Observable<any> {
    const payload: any = {
      group_id: config.group_id,
      season_id: config.season_id || '',
      assignment_method: config.assignment_method,
      flight_count: config.flight_count,
      flight_names_json: JSON.stringify(config.flight_names || []),
      index_boundaries_json: config.index_boundaries ? JSON.stringify(config.index_boundaries) : ''
    };
    const url = API_URL + 'ggkSaveGroupFlightConfig';
    return this.post<any>(url, payload);
  }

  // ── Group Player Flights ──────────────────────────────────────────────

  ggkGetGroupPlayerFlights(group_id: string, season_id?: string): Observable<any> {
    const payload: any = { group_id: parseInt(group_id, 10) || 0, season_id: season_id || '' };
    const url = API_URL + 'ggkGetGroupPlayerFlights';
    return this.post<any>(url, payload);
  }

  ggkSaveGroupPlayerFlights(group_id: string, assignments: { player_id: string; flight_key: string }[], season_id?: string): Observable<any> {
    const payload: any = { group_id: group_id, assignments: assignments };
    if (season_id) { payload.season_id = season_id; }
    const url = API_URL + 'ggkSaveGroupPlayerFlights';
    return this.post<any>(url, payload);
  }

  ggkAutoAssignByIndex(group_id: string, season_id?: string): Observable<any> {
    const payload: any = { group_id: parseInt(group_id, 10) || 0, season_id: season_id || '' };
    const url = API_URL + 'ggkAutoAssignByIndex';
    return this.post<any>(url, payload);
  }

  // ── Resolve Flights From League Config ────────────────────────────────

  /**
   * Resolves flight assignments from league-level config (group_flight_config + group_player_flight)
   * into a specific game's game_config_json. Used for Fixed by Player mode in prepare-flights.
   */
  ggkResolveFlightsFromLeagueConfig(group_id: string, event_id: string, game_id: number, season_id?: string): Observable<any> {
    const payload: any = { group_id: parseInt(group_id, 10), event_id: parseInt(event_id, 10), game_id, season_id: season_id || '' };
    const url = API_URL + 'ggkResolveFlightsFromLeagueConfig';
    return this.post<any>(url, payload);
  }

}
